import { useState, useCallback } from 'react';
import { Room, LocalVideoTrack, LocalAudioTrack } from 'twilio-video';
import type { MediaState } from '../types';

interface UseMediaControlsOptions {
  room: Room | undefined;
}

export const useMediaControls = ({ room }: UseMediaControlsOptions) => {
  const [mediaState, setMediaState] = useState<MediaState>({
    isVideoEnabled: true,
    isAudioEnabled: true,
    isSpeakerEnabled: true,
    isScreenSharing: false,
  });

  const toggleVideo = useCallback(() => {
    if (!room) return;

    room.localParticipant.videoTracks.forEach((publication) => {
      const track = publication.track as LocalVideoTrack;
      if (track.isEnabled) track.disable();
      else track.enable();
    });

    setMediaState((prev) => ({ ...prev, isVideoEnabled: !prev.isVideoEnabled }));
  }, [room]);

  const toggleAudio = useCallback(() => {
    if (!room) return;

    room.localParticipant.audioTracks.forEach((publication) => {
      const track = publication.track as LocalAudioTrack;
      if (track.isEnabled) {
        track.disable();
      } else {
        track.enable();
      }
    });

    setMediaState((prev) => ({ ...prev, isAudioEnabled: !prev.isAudioEnabled }));
  }, [room]);

  const toggleSpeaker = useCallback(() => {
    setMediaState((prev) => ({ ...prev, isSpeakerEnabled: !prev.isSpeakerEnabled }));
  }, []);

  const toggleScreenShare = useCallback(async () => {
    if (!room) return;

    if (mediaState.isScreenSharing) {
      room.localParticipant.videoTracks.forEach((publication) => {
        if (publication.track.name === 'screen-share') {
          publication.track.stop();
          room.localParticipant.unpublishTrack(publication.track);
        }
      });
      setMediaState((prev) => ({ ...prev, isScreenSharing: false }));
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = new LocalVideoTrack(stream.getTracks()[0], {
          name: 'screen-share',
        });

        room.localParticipant.publishTrack(screenTrack);

        screenTrack.mediaStreamTrack.onended = () => {
          room.localParticipant.unpublishTrack(screenTrack);
          setMediaState((prev) => ({ ...prev, isScreenSharing: false }));
        };

        setMediaState((prev) => ({ ...prev, isScreenSharing: true }));
      } catch (error) {
        console.error('Screen share failed:', error);
      }
    }
  }, [room, mediaState.isScreenSharing]);

  return {
    mediaState,
    toggleVideo,
    toggleAudio,
    toggleSpeaker,
    toggleScreenShare,
  };
};
